import React, { useContext } from 'react'

import ParseActivityPanel from '../checkin/ParseActivityPanel'
import EffortChip from '../EffortChip'
import { UserContext } from '../UserContext'
import type { HgChatItem } from '../../api/client'
import type { ParseActivity } from '../../hooks/useParseActivity'
import { emptyParseActivity } from '../../hooks/useParseActivity'
import { isTurnLive, type HgTranscript } from '../../hooks/useHgConversation'
import ChatItemView, {
    AssistantMessage,
    ChatNote,
    PersonaBubble,
    UserBubble,
    type DecideProposal,
} from './ChatItemView'

/** Stands in until the turn's first event arrives, so the line is up at once. */
const TURN_IDLE: ParseActivity = emptyParseActivity(0)

const itemKey = (item: HgChatItem): string => {
    const clientKey = item.content?.client_key
    // The same key the pending bubble had, so the echo replaces it in place.
    return typeof clientKey === 'string' && clientKey ? `send:${clientKey}` : `seq:${item.seq}`
}

interface ChatTranscriptProps {
    transcript: HgTranscript
    activity: ParseActivity | null
    onToggleActivity: (expanded: boolean) => void
    onToggleTechnical: (technical: boolean) => void
    onDecide?: DecideProposal
}

const ChatTranscript: React.FC<ChatTranscriptProps> = ({
    transcript,
    activity,
    onToggleActivity,
    onToggleTechnical,
    onDecide,
}) => {
    const { user } = useContext(UserContext)
    const { items, pending, streaming, turn } = transcript
    const live = isTurnLive(turn)
    const streams = Object.values(streaming)
    const lastItem = items[items.length - 1]
    const lastWasInterrupted =
        (lastItem?.item_type === 'assistant_message' || lastItem?.item_type === 'persona_message') &&
        Boolean(lastItem.content?.interrupted)
    // Whoever is taking the turn's step is who the streaming text is from.
    const speaker =
        turn?.speaker_persona_id != null
            ? transcript.conversation?.personas?.find((p) => p.persona_id === turn.speaker_persona_id)
            : undefined

    // A summary stands where the messages it replaced were, ahead of the
    // ones it kept, though it was written after them.
    const shown = [
        ...items.filter((i) => i.item_type === 'compaction_summary'),
        ...items.filter((i) => i.item_type !== 'compaction_summary'),
    ]

    return (
        <div className="flex flex-col" data-testid="chat-transcript">
            {shown.map((item) => (
                <ChatItemView key={itemKey(item)} item={item} items={items} onDecide={onDecide} />
            ))}
            {pending.map((p) => (
                <UserBubble key={`send:${p.clientKey}`} text={p.text} pending />
            ))}
            {streams.map((s) =>
                speaker ? (
                    <PersonaBubble
                        key={`stream:${s.itemKey}`}
                        name={speaker.name}
                        imageUrl={speaker.image_url}
                        text={s.text}
                        streaming
                    />
                ) : (
                    <AssistantMessage key={`stream:${s.itemKey}`} text={s.text} streaming />
                ),
            )}
            {live && turn && (
                <div className="mb-4 flex flex-col gap-1" data-testid="chat-turn-activity">
                    <ParseActivityPanel
                        activity={activity ?? TURN_IDLE}
                        idleLabel="Thinking"
                        onToggleExpanded={onToggleActivity}
                        onToggleTechnical={onToggleTechnical}
                    />
                    {user?.isAdmin && turn.model && (
                        <div
                            className="flex items-center gap-2 text-xs text-gray-500"
                            data-testid="chat-turn-model"
                        >
                            <span className="font-mono">{turn.model}</span>
                            <EffortChip effort={turn.effort} />
                        </div>
                    )}
                </div>
            )}
            {/* A step limit has its own notice in the transcript. */}
            {turn?.status === 'error' && turn.error_code !== 'step_limit' && (
                <ChatNote tone="error">Something went wrong. Try again.</ChatNote>
            )}
            {turn?.status === 'cancelled' && !lastWasInterrupted && <ChatNote>Stopped</ChatNote>}
        </div>
    )
}

export default ChatTranscript
